import {
  type AttendanceShiftsByDay,
  type AttendanceWeekday,
  DayCenterSchedule,
  shiftModeFor,
} from '@seen/core/models/schedules/dayCenterSchedule'
import { createReferenceStringWithHistory } from '@seen/core/references'
import { isEqual, sortBy } from 'lodash-es'
import type { ReactNode } from 'react'
import { ActivityEntry } from '#src/components/activity/ActivityEntry.tsx'
import {
  formatAttendanceDays,
  shiftModeLabel,
} from '#src/components/patient/detail-content/day-center-schedule/weekdays.ts'
import { useResourceVersions } from '#src/hooks/useResourceVersions.ts'

// Builds an activity-log feed (newest-first) from a day-center schedule's FHIR
// version history. Each meaningful version becomes an `ActivityEntry` whose
// saved schedule note renders below the change description when present.
export function useDayCenterScheduleActivity(
  scheduleId: string | undefined
): ReactNode[] {
  const { data: versions = [] } = useResourceVersions(
    DayCenterSchedule,
    scheduleId
  )

  const ascending = sortBy(versions, (version) => version.lastUpdated)

  const entries: ReactNode[] = []
  ascending.forEach((version, index) => {
    const previous = index > 0 ? ascending[index - 1] : undefined
    const action = describeChange(previous, version)
    if (!action) {
      return
    }

    entries.push(
      <ActivityEntry
        key={createReferenceStringWithHistory(version.toFhirResource())}
        author={version.resourceAuthor}
        time={version.lastUpdated}
        action={action}
      >
        {version.note && version.note !== previous?.note && (
          <div className="rounded-md bg-muted px-2 py-1 text-xs whitespace-pre-wrap text-foreground-secondary">
            {version.note}
          </div>
        )}
      </ActivityEntry>
    )
  })

  return entries.reverse()
}

function shiftPhrase(
  shifts: AttendanceShiftsByDay,
  days: AttendanceWeekday[]
): string | undefined {
  const mode = shiftModeFor(shifts, days)
  return mode ? shiftModeLabel(mode) : undefined
}

// Every field the schedule modal can edit needs a case here: a version whose
// only change goes undescribed is dropped from the feed entirely.
function describeChange(
  previous: DayCenterSchedule | undefined,
  current: DayCenterSchedule
): string | undefined {
  const daysSummary = formatAttendanceDays(current.attendanceDays) || 'no days'

  if (!previous) {
    return `created the schedule (${daysSummary})`
  }

  if (!isEqual(previous.attendanceDays, current.attendanceDays)) {
    return `updated days to ${daysSummary}`
  }

  if (!isEqual(previous.attendanceShifts, current.attendanceShifts)) {
    const phrase = shiftPhrase(current.attendanceShifts, current.attendanceDays)
    return phrase
      ? `updated the day center shift to ${phrase}`
      : 'cleared the day center shift'
  }

  if (
    !isEqual(
      previous.preferredAppointmentDays,
      current.preferredAppointmentDays
    )
  ) {
    const preferred = formatAttendanceDays(current.preferredAppointmentDays)
    return preferred
      ? `updated preferred appointment days to ${preferred}`
      : 'cleared the preferred appointment days'
  }

  if (
    !isEqual(
      previous.preferredAppointmentShifts,
      current.preferredAppointmentShifts
    )
  ) {
    const phrase = shiftPhrase(
      current.preferredAppointmentShifts,
      current.preferredAppointmentDays
    )
    return phrase
      ? `updated the preferred appointment shift to ${phrase}`
      : 'cleared the preferred appointment shift'
  }

  if (previous.note !== current.note) {
    return current.note ? 'added a note' : 'cleared the note'
  }

  // Metadata-only version (e.g. a tag touch) — nothing worth showing.
  return undefined
}
